import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Eye, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-white">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white shadow-sm border-b border-secondary-200"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-secondary-600 hover:text-primary-600 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <div className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-primary-600" />
              <h1 className="text-2xl font-bold text-secondary-900">Privacy Policy</h1>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-8"
        >
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-secondary-900 mb-4">Privacy Policy Guidelines For Prasadji Games Studio</h2>
              <p className="text-secondary-600 mb-6">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">Introduction</h3>
                <p className="text-secondary-600 leading-relaxed">
                  This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.
                </p>
                <p className="text-secondary-600 leading-relaxed mt-4">
                  We use your data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, terms used in this Privacy Policy have the same meanings as in our Terms and Conditions.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
                  <Lock className="w-5 h-5 mr-2 text-primary-600" />
                  Information Collection And Use
                </h3>
                <p className="text-secondary-600 leading-relaxed">
                  We collect several different types of information for various purposes to provide and improve our Service to you.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-primary-600" />
                  Tracking & Cookies Data
                </h3>
                <p className="text-secondary-600 leading-relaxed">
                  We use cookies and similar tracking technologies to track the activity on our Service and hold certain information.
                </p>
                <p className="text-secondary-600 leading-relaxed mt-4">
                  Cookies are files with small amount of data which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device. Tracking technologies also used are beacons, tags, and scripts to collect and track information and to improve and analyze our Service.
                </p>
                <p className="text-secondary-600 leading-relaxed mt-4">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-primary-600" />
                  Types of Data Collected
                </h3>
                
                <h4 className="text-lg font-semibold text-secondary-900 mb-3 mt-6">Personal Data</h4>
                <p className="text-secondary-600 leading-relaxed">
                  While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:
                </p>
                
                <h4 className="text-lg font-semibold text-secondary-900 mb-3 mt-6">Usage Data</h4>
                <p className="text-secondary-600 leading-relaxed">
                  We may also collect information that your browser sends whenever you visit our Service or when you access the Service by or through a mobile device ("Usage Data").
                </p>
                <p className="text-secondary-600 leading-relaxed mt-4">
                  This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
                </p>
                <p className="text-secondary-600 leading-relaxed mt-4">
                  When you access the Service by or through a mobile device, this Usage Data may include information such as the type of mobile device you use, your mobile device unique ID, the IP address of your mobile device, your mobile operating system, the type of mobile Internet browser you use, unique device identifiers and other diagnostic data.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">Use of Data</h3>
                <p className="text-secondary-600 leading-relaxed mb-4">
                  Prasadji Games Studio uses the collected data for various purposes:
                </p>
                <ul className="list-disc list-inside space-y-2 text-secondary-600">
                  <li>To provide and maintain the Service</li>
                  <li>To notify you about changes to our Service</li>
                  <li>To provide customer care and support</li>
                  <li>To provide analysis or valuable information so that we can improve the Service</li>
                  <li>To monitor the usage of the Service</li>
                  <li>To detect, prevent and address technical issues</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-primary-600" />
                  Disclosure Of Data
                </h3>
                
                <h4 className="text-lg font-semibold text-secondary-900 mb-3 mt-6">Legal Requirements</h4>
                <p className="text-secondary-600 leading-relaxed">
                  Prasadji Games Studio may disclose your Personal Data in the good faith belief that such action is necessary to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-secondary-600 mt-4">
                  <li>To comply with a legal obligation</li>
                  <li>To protect and defend the rights or property of Prasadji Games Studio</li>
                  <li>To protect the personal safety of users of the Service or the public</li>
                  <li>To protect against legal liability</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">Transfer Of Data</h3>
                <p className="text-secondary-600 leading-relaxed">
                  Your information, including Personal Data, may be transferred to and maintained on computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.
                </p>
                <p className="text-secondary-600 leading-relaxed mt-4">
                  Prasadji Games Studio will take all steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy and no transfer of your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of your data and other personal information.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">Security Of Data</h3>
                <p className="text-secondary-600 leading-relaxed">
                  The security of your data is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">Contact Us</h3>
                <p className="text-secondary-600 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-secondary-50 rounded-lg">
                  <p className="text-secondary-700">
                    <strong>Developer:</strong> Prasadji Games Studio
                  </p>
                  <p className="text-secondary-700 mt-2">
                    <strong>Email:</strong> hu477418@gmail.com
                  </p>
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy; 